// console.log('product-upsell-handler.js loading...');

/**
 * Handles the product upsell functionality with checkboxes
 * Modern implementation that works with native form submission
 */
class ProductUpsellHandler {
  constructor() {
    this.selectedUpsells = new Map();
    this.isInitialized = false;
    this.initTimeout = null;
    this.cartUpdateTimeout = null;

    // Store bound handlers for cleanup
    this.boundHandlers = {
      setupListeners: this.setupListeners.bind(this),
      handleChange: this.handleChange.bind(this),
      handleVariantSelected: this.handleVariantSelected.bind(this),
      handleInlineChange: this.handleInlineChange.bind(this),
      handleCartUpdate: this.handleCartUpdate.bind(this)
    };

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.boundHandlers.setupListeners);
    } else {
      this.setupListeners();
    }
  }

  handleChange(event) {
    if (event.target.classList.contains('product-upsell__checkbox')) {
      // console.log('Checkbox change detected:', event.target);
      this.handleCheckboxChange(event.target);
    }
  }

  handleVariantSelected(event) {
    const variantPicker = event.target.closest('variant-picker');
    if (!variantPicker || !variantPicker.dataset.upsellIndex) return;

    const upsellBlock = variantPicker.closest('[data-upsell-block-id]');
    if (!upsellBlock) return;

    const blockId = upsellBlock.dataset.upsellBlockId;
    const upsellIndex = variantPicker.dataset.upsellIndex;
    const checkbox = upsellBlock.querySelector(`#upsell-${blockId}-${upsellIndex}`);

    if (checkbox && checkbox.checked && event.detail && event.detail.variant) {
      const key = `${blockId}-${upsellIndex}`;
      if (this.selectedUpsells.has(key)) {
        const upsellData = this.selectedUpsells.get(key);
        upsellData.variantId = event.detail.variant.id;
      }
    }
  }

  handleInlineChange(event) {
    if (event.target.classList.contains('product-upsell-inline__variant-select')) {
      const select = event.target;
      const productId = select.dataset.upsellProductId;
      const checkbox = document.querySelector(`.product-upsell__checkbox[data-product-id="${productId}"]`);

      if (checkbox) {
        const newVariantId = select.value;
        // console.log('Inline upsell variant changed:', newVariantId);
        checkbox.dataset.variantId = newVariantId;

        // Auto-select matching selling plan
        this.autoSelectSellingPlan(select, productId);

        // If checkbox is checked, update the stored data and notify
        const key = `inline-${productId}`;
        if (this.selectedUpsells.has(key)) {
          // console.log('Updating inline upsell variant to:', newVariantId);
          const upsellData = this.selectedUpsells.get(key);
          upsellData.variantId = newVariantId;
          upsellData.sellingPlanId = checkbox.dataset.sellingPlanId;
          this.dispatchUpsellChangeEvent();
        }
      }
    }

    // Listen for selling plan changes
    if (event.target.classList.contains('product-upsell-inline__plan-select')) {
      const select = event.target;
      const productId = select.dataset.upsellProductId;
      const checkbox = document.querySelector(`.product-upsell__checkbox[data-product-id="${productId}"]`);

      if (checkbox) {
        const newSellingPlanId = select.value;
        // console.log('Inline upsell selling plan changed:', newSellingPlanId);
        checkbox.dataset.sellingPlanId = newSellingPlanId;

        // If checkbox is checked, update the stored data and notify
        const key = `inline-${productId}`;
        if (this.selectedUpsells.has(key)) {
          const upsellData = this.selectedUpsells.get(key);
          upsellData.sellingPlanId = newSellingPlanId;
          this.dispatchUpsellChangeEvent();
        }
      }
    }
  }

  setupListeners() {
    // Listen for checkbox changes in all upsell blocks
    document.addEventListener('change', this.boundHandlers.handleChange);

    // Listen for variant changes to update stored variant IDs
    document.addEventListener('variant:selected', this.boundHandlers.handleVariantSelected);

    // Listen for inline upsell variant changes using event delegation
    document.addEventListener('change', this.boundHandlers.handleInlineChange);

    // Initialize any inline upsells that are already on the page
    this.initTimeout = setTimeout(() => {
      const inlineCheckboxes = document.querySelectorAll('.product-upsell-inline .product-upsell__checkbox');
      // console.log('Looking for inline upsell checkboxes, found:', inlineCheckboxes.length);

      inlineCheckboxes.forEach(checkbox => {
        const productId = checkbox.dataset.productId;

        // Auto-select the correct selling plan based on the default variant
        const variantSelect = document.querySelector(`.product-upsell-inline__variant-select[data-upsell-product-id="${productId}"]`);
        if (variantSelect) {
          this.autoSelectSellingPlan(variantSelect, productId);
        }

        // Set up the checkbox to work with the handler
        if (checkbox.checked) {
          this.handleCheckboxChange(checkbox, true);
          this.dispatchUpsellChangeEvent();
        }
      });
    }, 500);

    // Clear selections when cart is successfully updated
    document.addEventListener('cart:update', this.boundHandlers.handleCartUpdate);

    // Mark as initialized
    this.isInitialized = true;
    
    // Check if any checkboxes are already checked (from page state)
    const checkedBoxes = document.querySelectorAll('.product-upsell__checkbox:checked');
    if (checkedBoxes.length > 0) {
      // Process pre-checked boxes
      checkedBoxes.forEach(checkbox => {
        this.handleCheckboxChange(checkbox, true);
      });
      // Only dispatch if we actually have selections
      this.dispatchUpsellChangeEvent();
    }
  }

  handleCheckboxChange(checkbox, silent = false) {
    const upsellBlock = checkbox.closest('[data-upsell-block-id]');
    const inlineUpsell = checkbox.closest('.product-upsell-inline');

    let key;

    if (upsellBlock) {
      const blockId = upsellBlock.dataset.upsellBlockId;
      const upsellIndex = checkbox.closest('[data-upsell-product-index]').dataset.upsellProductIndex;
      key = `${blockId}-${upsellIndex}`;
    } else if (inlineUpsell) {
      key = `inline-${checkbox.dataset.productId}`;
    } else {
      // console.log('Upsell checkbox not in recognized container');
      return;
    }

    if (checkbox.checked) {
      // console.log('Adding upsell to cart selection:', checkbox.dataset.variantId);
      const upsellData = {
        variantId: checkbox.dataset.variantId,
        productId: checkbox.dataset.productId,
        quantity: 1
      };

      // Add selling plan if it exists
      if (checkbox.dataset.sellingPlanId) {
        upsellData.sellingPlanId = checkbox.dataset.sellingPlanId;
        // console.log('Upsell has selling plan:', checkbox.dataset.sellingPlanId);
      }

      this.selectedUpsells.set(key, upsellData);
    } else {
      // console.log('Removing upsell from cart selection');
      this.selectedUpsells.delete(key);
    }

    // Dispatch event to notify form component unless silent
    if (!silent && this.isInitialized) {
      this.dispatchUpsellChangeEvent();
    }
  }

  handleCartUpdate(event) {
    // Only clear if the event came from a product form submission
    if (event.detail && event.detail.data && event.detail.data.source === 'product-form-component') {
      // Small delay to ensure the cart update is complete
      // Clear any existing timeout first
      if (this.cartUpdateTimeout) {
        clearTimeout(this.cartUpdateTimeout);
      }
      this.cartUpdateTimeout = setTimeout(() => {
        this.clearSelections();
        this.cartUpdateTimeout = null;
      }, 100);
    }
  }

  destroy() {
    // Remove all event listeners to prevent memory leaks
    document.removeEventListener('DOMContentLoaded', this.boundHandlers.setupListeners);
    document.removeEventListener('change', this.boundHandlers.handleChange);
    document.removeEventListener('variant:selected', this.boundHandlers.handleVariantSelected);
    document.removeEventListener('change', this.boundHandlers.handleInlineChange);
    document.removeEventListener('cart:update', this.boundHandlers.handleCartUpdate);

    // Clear timeouts if they exist
    if (this.initTimeout) {
      clearTimeout(this.initTimeout);
      this.initTimeout = null;
    }
    if (this.cartUpdateTimeout) {
      clearTimeout(this.cartUpdateTimeout);
      this.cartUpdateTimeout = null;
    }
  }

  dispatchUpsellChangeEvent() {
    // Only dispatch if we have selections or if we're clearing (for cart update)
    const selectedItems = this.getSelectedUpsellItems();
    // console.log('Dispatching upsells:change event with items:', selectedItems);
    const event = new CustomEvent('upsells:change', {
      detail: {
        selectedUpsells: selectedItems
      },
      bubbles: true
    });
    document.dispatchEvent(event);
    // console.log('upsells:change event dispatched');
  }

  getSelectedUpsellItems() {
    const items = [];
    for (const [key, product] of this.selectedUpsells) {
      const item = {
        id: parseInt(product.variantId),
        quantity: product.quantity
      };

      // Add selling plan if it exists
      if (product.sellingPlanId) {
        item.selling_plan = parseInt(product.sellingPlanId);
        // console.log(`Upsell item: variant=${product.variantId}, selling_plan=${product.sellingPlanId}`);
      } else {
        // console.log(`Upsell item: variant=${product.variantId}`);
      }

      items.push(item);
    }
    // console.log('Selected upsell items to add to cart:', items);
    return items;
  }

  clearSelections() {
    // For inline upsells, keep them checked and re-add them
    const inlineCheckboxes = document.querySelectorAll('.product-upsell-inline .product-upsell__checkbox:checked');

    // Uncheck all non-inline checkboxes
    document.querySelectorAll('.product-upsell__checkbox:checked').forEach(checkbox => {
      const isInline = checkbox.closest('.product-upsell-inline');
      if (!isInline) {
        checkbox.checked = false;
        const item = checkbox.closest('.product-upsell__item');
        if (item) {
          item.classList.remove('product-upsell__item--checked');
        }
      }
    });

    // Clear the selected products map
    this.selectedUpsells.clear();

    // Re-add inline checkboxes that should stay checked
    inlineCheckboxes.forEach(checkbox => {
      this.handleCheckboxChange(checkbox, true);
    });

    // Notify that selections have been updated
    this.dispatchUpsellChangeEvent();
  }

  hasSelectedUpsells() {
    return this.selectedUpsells.size > 0;
  }

  autoSelectSellingPlan(variantSelect, productId) {
    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    const variantText = selectedOption.textContent.toLowerCase();
    const variantTitle = selectedOption.dataset.variantTitle?.toLowerCase() || variantText;
    const variantPrice = selectedOption.dataset.variantPrice;
    const planSelect = document.querySelector(`.product-upsell-inline__plan-select[data-upsell-product-id="${productId}"]`);
    const checkbox = document.querySelector(`.product-upsell__checkbox[data-product-id="${productId}"]`);

    if (!planSelect) return;

    // Check for subscription patterns more precisely
    // Look for "recurring" keyword to identify actual subscriptions
    const isRecurringSubscription = variantText.includes('recurring');

    // Check for monthly subscription patterns
    const isMonthlyVariant = isRecurringSubscription && (
                            variantText.includes('monthly') ||
                            variantText.includes('/mo') ||
                            variantText.includes('month recurring'));

    // Check for annual subscription patterns
    const isAnnualVariant = isRecurringSubscription && (
                           variantText.includes('annual') ||
                           variantText.includes('year recurring') ||
                           variantText.includes('yearly'));

    let matchingPlanFound = false;

    for (let i = 0; i < planSelect.options.length; i++) {
      const option = planSelect.options[i];
      const subscriptionType = option.dataset.subscriptionType;

      if (isMonthlyVariant && subscriptionType === 'monthly') {
        planSelect.selectedIndex = i;
        if (checkbox) checkbox.dataset.sellingPlanId = option.value;
        matchingPlanFound = true;
        // console.log('Auto-selected monthly selling plan for variant:', variantText);
        break;
      } else if (isAnnualVariant && subscriptionType === 'annual') {
        planSelect.selectedIndex = i;
        if (checkbox) checkbox.dataset.sellingPlanId = option.value;
        matchingPlanFound = true;
        // console.log('Auto-selected annual selling plan for variant:', variantText);
        break;
      }
    }

    if (!matchingPlanFound) {
      planSelect.selectedIndex = 0;
      if (checkbox) checkbox.dataset.sellingPlanId = planSelect.value;
      // console.log('No matching plan found, selected first option');
    }
  }
}

// Initialize the handler
if (!window.productUpsellHandler) {
  // console.log('Initializing ProductUpsellHandler');
  window.productUpsellHandler = new ProductUpsellHandler();
  // console.log('ProductUpsellHandler initialized:', window.productUpsellHandler);
} else {
  // console.log('ProductUpsellHandler already exists');
}

// Export for ES module compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductUpsellHandler;
}